import { getLocale, translate } from '../../i18n'
import { readThemeColor } from '../../state/colorThemeStore'
import type {
   WeightChartMode,
   WeightEntry,
   WeightFilterKey,
} from '../../state/weightStore'

export type WeightTrendAnalysis = {
   aggregated: WeightEntry[]
   slopePerDay: number | null
   intercept: number | null
   baseTimestamp: number | null
   lastTimestamp: number | null
   projectedDaysFromLast: number | null
   projectedTimestamp: number | null
   targetWeightKg: number | null
   latestWeight: number | null
   latestTimestamp: number | null
}

export type DrawWeightChartOptions = {
   dataPoints: WeightEntry[]
   filterKey: WeightFilterKey
   average: number | null
   heightMeters: number | null
   targetWeightKg: number | null
   showTarget: boolean
   chartMode: WeightChartMode
   trendAnalysis: WeightTrendAnalysis | null
   /** Formats a kg value for the axis in the active unit system. */
   formatMassLabel?: (weightKg: number) => string
}

/** A measurement dot as drawn, in canvas backing-store pixels. */
export type WeightChartPoint = {
   x: number
   y: number
   entry: WeightEntry
}

const MS_IN_DAY = 24 * 60 * 60 * 1000
const CHART_PADDING_LEFT = 68
const CHART_PADDING_RIGHT = 32
const CHART_PADDING_Y = 32
const WEIGHT_AXIS_TICK_COUNT = 5

/**
 * Computes evenly spaced weight-axis tick values.
 *
 * @param minWeight - The minimum weight.
 * @param maxWeight - The maximum weight.
 * @param tickCount - Desired number of ticks (defaults to 5).
 * @returns The tick values.
 */
export function computeWeightAxisTicks(
   minWeight: number,
   maxWeight: number,
   tickCount = WEIGHT_AXIS_TICK_COUNT,
) {
   if (!Number.isFinite(minWeight) || !Number.isFinite(maxWeight)) {
      return []
   }

   const start = Math.min(minWeight, maxWeight)
   const end = Math.max(minWeight, maxWeight)
   const count = Math.max(2, Math.floor(tickCount))
   if (Math.abs(end - start) < 0.001) {
      return [start]
   }

   const step = (end - start) / (count - 1)
   return Array.from({ length: count }, (_, index) => start + step * index)
}

/**
 * Chooses a smoothing window (in days) based on the visible range and point count.
 *
 * @param rangeDays - The visible range in days.
 * @param pointCount - Number of data points.
 * @returns The smoothing window in days.
 */
function determineSmoothingWindowDays(rangeDays: number, pointCount: number) {
   if (!Number.isFinite(rangeDays) || rangeDays <= 0) {
      return 1
   }
   if (rangeDays <= 14) {
      return pointCount >= 6 ? 2 : 1
   }
   if (rangeDays <= 45) {
      return 5
   }
   if (rangeDays <= 120) {
      return 7
   }
   if (rangeDays <= 240) {
      return 10
   }
   if (rangeDays <= 365) {
      return 14
   }
   return 21
}

/**
 * Builds a smoothed weight series by moving-average sampling for the scatter-trend curve.
 *
 * @param dataPoints - The weight entries.
 * @param minTimestamp - The range start.
 * @param maxTimestamp - The range end.
 * @returns The smoothed points.
 */
function buildSmoothedCurveSeries(
   dataPoints: WeightEntry[],
   minTimestamp: number,
   maxTimestamp: number,
) {
   const validPoints = dataPoints
      .filter(
         point =>
            point && Number.isFinite(point.timestamp) && Number.isFinite(point.weight),
      )
      .map(point => ({
         timestamp: point.timestamp,
         weight: point.weight,
      }))
      .sort((a, b) => a.timestamp - b.timestamp)

   if (validPoints.length < 2) {
      return validPoints
   }

   const rangeMs = Math.max(maxTimestamp - minTimestamp, MS_IN_DAY)
   const rangeDays = rangeMs / MS_IN_DAY
   const smoothingWindowDays = determineSmoothingWindowDays(rangeDays, validPoints.length)
   const windowMs = Math.max(smoothingWindowDays * MS_IN_DAY, MS_IN_DAY)
   const targetSpacingDays = Math.max(smoothingWindowDays * 0.5, 1)
   const approxSampleCount = Math.round(rangeDays / targetSpacingDays)
   const sampleCount = Math.min(
      24,
      Math.max(3, approxSampleCount, Math.min(6, validPoints.length + 1)),
   )
   const stepMs = sampleCount > 1 ? rangeMs / Math.max(sampleCount - 1, 1) : rangeMs

   const samples: Array<{ timestamp: number; weight: number }> = []
   for (let index = 0; index < sampleCount; index += 1) {
      let targetTimestamp =
         sampleCount === 1 ? validPoints[0]!.timestamp : minTimestamp + stepMs * index
      if (!Number.isFinite(targetTimestamp)) {
         targetTimestamp = validPoints[Math.min(index, validPoints.length - 1)]!.timestamp
      }
      targetTimestamp = Math.min(Math.max(targetTimestamp, minTimestamp), maxTimestamp)

      const windowStart = targetTimestamp - windowMs / 2
      const windowEnd = targetTimestamp + windowMs / 2
      const windowWeights = validPoints
         .filter(point => point.timestamp >= windowStart && point.timestamp <= windowEnd)
         .map(point => point.weight)

      let averagedWeight: number
      if (windowWeights.length > 0) {
         averagedWeight =
            windowWeights.reduce((sum, weight) => sum + weight, 0) / windowWeights.length
      } else {
         let nearest = validPoints[0]!
         let nearestDistance = Math.abs(nearest.timestamp - targetTimestamp)
         for (
            let candidateIndex = 1;
            candidateIndex < validPoints.length;
            candidateIndex += 1
         ) {
            const candidate = validPoints[candidateIndex]!
            const candidateDistance = Math.abs(candidate.timestamp - targetTimestamp)
            if (candidateDistance < nearestDistance) {
               nearest = candidate
               nearestDistance = candidateDistance
            }
         }
         averagedWeight = nearest.weight
      }

      samples.push({ timestamp: targetTimestamp, weight: averagedWeight })
   }

   samples[0]!.timestamp = validPoints[0]!.timestamp
   samples[samples.length - 1]!.timestamp = validPoints[validPoints.length - 1]!.timestamp

   const deduped: Array<{ timestamp: number; weight: number }> = []
   for (const sample of samples) {
      const lastSample = deduped[deduped.length - 1]
      if (
         lastSample &&
         Math.abs(sample.timestamp - lastSample.timestamp) < 1000 &&
         Math.abs(sample.weight - lastSample.weight) < 0.01
      ) {
         continue
      }
      deduped.push(sample)
   }

   return deduped.length >= 2 ? deduped : validPoints
}

/**
 * Draws a smooth curve through canvas points.
 *
 * @param ctx - The canvas context.
 * @param points - The points to connect.
 */
function drawCurvedLine(
   ctx: CanvasRenderingContext2D,
   points: Array<{ x: number; y: number }>,
) {
   if (points.length === 0) {
      return
   }
   if (points.length === 1) {
      ctx.beginPath()
      ctx.moveTo(points[0]!.x, points[0]!.y)
      ctx.lineTo(points[0]!.x, points[0]!.y)
      ctx.stroke()
      return
   }
   if (points.length === 2) {
      ctx.beginPath()
      ctx.moveTo(points[0]!.x, points[0]!.y)
      ctx.lineTo(points[1]!.x, points[1]!.y)
      ctx.stroke()
      return
   }

   const tension = 1 / 6
   ctx.beginPath()
   ctx.moveTo(points[0]!.x, points[0]!.y)
   for (let index = 0; index < points.length - 1; index += 1) {
      const p0 = index > 0 ? points[index - 1]! : points[index]!
      const p1 = points[index]!
      const p2 = points[index + 1]!
      const p3 = index + 2 < points.length ? points[index + 2]! : p2
      const cp1x = p1.x + (p2.x - p0.x) * tension
      const cp1y = p1.y + (p2.y - p0.y) * tension
      const cp2x = p2.x - (p3.x - p1.x) * tension
      const cp2y = p2.y - (p3.y - p1.y) * tension
      ctx.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, p2.x, p2.y)
   }
   ctx.stroke()
}

/**
 * Returns the local-midnight timestamp for a time.
 *
 * @param timestamp - The epoch-ms timestamp.
 * @returns Epoch ms at local midnight.
 */
function getDayStartTimestamp(timestamp: number) {
   const date = new Date(timestamp)
   date.setHours(0, 0, 0, 0)
   return date.getTime()
}

/**
 * Returns the local week-start (Monday) timestamp for a time.
 *
 * @param timestamp - The epoch-ms timestamp.
 * @returns The week-start timestamp.
 */
function getWeekStartTimestamp(timestamp: number) {
   const date = new Date(getDayStartTimestamp(timestamp))
   const day = date.getDay()
   const offset = (day + 6) % 7
   date.setDate(date.getDate() - offset)
   return date.getTime()
}

/**
 * Returns the start of the following week.
 *
 * @param timestamp - The epoch-ms timestamp.
 * @returns The next week-start timestamp.
 */
function getNextWeekStartTimestamp(timestamp: number) {
   return getWeekStartTimestamp(timestamp) + 7 * MS_IN_DAY
}

/**
 * Returns the local month-start timestamp for a time.
 *
 * @param timestamp - The epoch-ms timestamp.
 * @returns The month-start timestamp.
 */
function getMonthStartTimestamp(timestamp: number) {
   const date = new Date(getDayStartTimestamp(timestamp))
   date.setDate(1)
   return date.getTime()
}

/**
 * Returns the start of the following month.
 *
 * @param timestamp - The epoch-ms timestamp.
 * @returns The next month-start timestamp.
 */
function getNextMonthStartTimestamp(timestamp: number) {
   const date = new Date(getMonthStartTimestamp(timestamp))
   date.setMonth(date.getMonth() + 1)
   return date.getTime()
}

/**
 * Returns the local quarter-start timestamp for a time.
 *
 * @param timestamp - The epoch-ms timestamp.
 * @returns The quarter-start timestamp.
 */
function getQuarterStartTimestamp(timestamp: number) {
   const date = new Date(getDayStartTimestamp(timestamp))
   const quarterMonth = Math.floor(date.getMonth() / 3) * 3
   date.setMonth(quarterMonth, 1)
   return date.getTime()
}

/**
 * Returns the start of the following quarter.
 *
 * @param timestamp - The epoch-ms timestamp.
 * @returns The next quarter-start timestamp.
 */
function getNextQuarterStartTimestamp(timestamp: number) {
   const date = new Date(getQuarterStartTimestamp(timestamp))
   date.setMonth(date.getMonth() + 3)
   return date.getTime()
}

/**
 * Returns the local year-start timestamp for a time.
 *
 * @param timestamp - The epoch-ms timestamp.
 * @returns The year-start timestamp.
 */
function getYearStartTimestamp(timestamp: number) {
   const date = new Date(getDayStartTimestamp(timestamp))
   date.setMonth(0, 1)
   return date.getTime()
}

/**
 * Returns the start of the following year.
 *
 * @param timestamp - The epoch-ms timestamp.
 * @returns The next year-start timestamp.
 */
function getNextYearStartTimestamp(timestamp: number) {
   const date = new Date(getYearStartTimestamp(timestamp))
   date.setFullYear(date.getFullYear() + 1)
   return date.getTime()
}

/**
 * Computes x-axis time gridline markers for a range and filter.
 *
 * @param rangeStartTimestamp - The range start.
 * @param rangeEndTimestamp - The range end.
 * @param filterKey - The active filter.
 * @returns The marker timestamps.
 */
function computeWeightChartTimeMarkers(
   rangeStartTimestamp: number,
   rangeEndTimestamp: number,
   filterKey: WeightFilterKey,
) {
   const start = Math.min(rangeStartTimestamp, rangeEndTimestamp)
   const end = Math.max(rangeStartTimestamp, rangeEndTimestamp)
   if (end <= start) {
      return []
   }

   /**
    * Builds a marker series by aligning to a boundary and advancing across the range.
    *
    * @param aligner - Aligns a timestamp to a boundary.
    * @param advancer - Advances to the next boundary.
    * @returns The marker timestamps.
    */
   const buildSeries = (
      aligner: (timestamp: number) => number,
      advancer: (timestamp: number) => number,
   ) => {
      const markers: number[] = []
      let marker = aligner(start)
      let iterations = 0
      while (marker < start && iterations < 512) {
         const next = advancer(marker)
         if (!Number.isFinite(next) || next <= marker) {
            break
         }
         marker = next
         iterations += 1
      }
      while (marker < end && iterations < 512) {
         markers.push(marker)
         const next = advancer(marker)
         if (!Number.isFinite(next) || next <= marker) {
            break
         }
         marker = next
         iterations += 1
      }
      return markers
   }

   if (filterKey === 'week' || filterKey === 'month') {
      return buildSeries(getWeekStartTimestamp, getNextWeekStartTimestamp)
   }
   if (filterKey === 'quarter') {
      return buildSeries(getMonthStartTimestamp, getNextMonthStartTimestamp)
   }
   if (filterKey === 'year') {
      return buildSeries(getQuarterStartTimestamp, getNextQuarterStartTimestamp)
   }
   if (filterKey === 'max') {
      const span = end - start
      if (span >= 365 * MS_IN_DAY) {
         return buildSeries(getYearStartTimestamp, getNextYearStartTimestamp)
      }
      if (span >= 90 * MS_IN_DAY) {
         return buildSeries(getMonthStartTimestamp, getNextMonthStartTimestamp)
      }
      return buildSeries(getWeekStartTimestamp, getNextWeekStartTimestamp)
   }

   return []
}

/**
 * Averages entries per local day. Timestamps are averaged too (not snapped to
 * midnight) so a fitted line lands on the plotted points.
 *
 * @param dataPoints - The weight entries.
 * @returns One averaged entry per day, sorted chronologically.
 */
function aggregateEntriesByDay(dataPoints: WeightEntry[]): WeightEntry[] {
   const buckets = new Map<
      number,
      { timestampSum: number; weightSum: number; count: number }
   >()
   for (const point of dataPoints) {
      const dayStart = getDayStartTimestamp(point.timestamp)
      const existing = buckets.get(dayStart)
      if (existing) {
         existing.timestampSum += point.timestamp
         existing.weightSum += point.weight
         existing.count += 1
      } else {
         buckets.set(dayStart, {
            timestampSum: point.timestamp,
            weightSum: point.weight,
            count: 1,
         })
      }
   }

   return Array.from(buckets.values())
      .map(bucket => ({
         timestamp: bucket.timestampSum / bucket.count,
         weight: bucket.weightSum / bucket.count,
      }))
      .sort((a, b) => a.timestamp - b.timestamp)
}

/**
 * Computes a daily-aggregated linear weight trend and target projection.
 *
 * @param dataPoints - The weight entries.
 * @param targetWeightKg - The target weight, or null.
 * @returns The trend analysis.
 */
export function computeWeightTrendAnalysis(
   dataPoints: WeightEntry[],
   targetWeightKg: number | null,
): WeightTrendAnalysis {
   const safeTarget =
      typeof targetWeightKg === 'number' && Number.isFinite(targetWeightKg) ?
         targetWeightKg
      :  null

   if (!dataPoints.length) {
      return {
         aggregated: [],
         slopePerDay: null,
         intercept: null,
         baseTimestamp: null,
         lastTimestamp: null,
         projectedDaysFromLast: null,
         projectedTimestamp: null,
         targetWeightKg: safeTarget,
         latestWeight: null,
         latestTimestamp: null,
      }
   }

   const aggregated = aggregateEntriesByDay(dataPoints)

   const latestPoint = dataPoints[dataPoints.length - 1] ?? null
   const latestWeight = latestPoint?.weight ?? null
   const latestTimestamp = latestPoint?.timestamp ?? null

   if (aggregated.length < 2) {
      return {
         aggregated,
         slopePerDay: null,
         intercept: null,
         baseTimestamp: aggregated[0]?.timestamp ?? null,
         lastTimestamp: aggregated[aggregated.length - 1]?.timestamp ?? null,
         projectedDaysFromLast: null,
         projectedTimestamp: null,
         targetWeightKg: safeTarget,
         latestWeight,
         latestTimestamp,
      }
   }

   const baseTimestamp = aggregated[0]!.timestamp
   const offsets = aggregated.map(point => (point.timestamp - baseTimestamp) / MS_IN_DAY)
   const meanX = offsets.reduce((sum, value) => sum + value, 0) / offsets.length
   const meanY =
      aggregated.reduce((sum, value) => sum + value.weight, 0) / aggregated.length

   let numerator = 0
   let denominator = 0
   for (let index = 0; index < offsets.length; index += 1) {
      const dx = offsets[index]! - meanX
      numerator += dx * (aggregated[index]!.weight - meanY)
      denominator += dx * dx
   }

   const slopePerDay = denominator > 0 ? numerator / denominator : 0
   const intercept = meanY - slopePerDay * meanX
   const lastTimestamp = aggregated[aggregated.length - 1]!.timestamp
   const lastOffset = (lastTimestamp - baseTimestamp) / MS_IN_DAY

   let projectedDaysFromLast: number | null = null
   let projectedTimestamp: number | null = null
   if (safeTarget !== null && Math.abs(slopePerDay) >= 0.0001) {
      const targetOffset = (safeTarget - intercept) / slopePerDay
      const delta = targetOffset - lastOffset
      if (Number.isFinite(delta)) {
         projectedDaysFromLast = delta
         projectedTimestamp = lastTimestamp + delta * MS_IN_DAY
      }
   }

   return {
      aggregated,
      slopePerDay: Number.isFinite(slopePerDay) ? slopePerDay : null,
      intercept: Number.isFinite(intercept) ? intercept : null,
      baseTimestamp,
      lastTimestamp,
      projectedDaysFromLast,
      projectedTimestamp,
      targetWeightKg: safeTarget,
      latestWeight,
      latestTimestamp,
   }
}

/**
 * Maps a pointer x-position over the chart to a formatted date label.
 *
 * @param canvas - The chart canvas.
 * @param clientX - The pointer client X.
 * @param entries - The weight entries.
 * @param rangeStartTimestamp - The range start.
 * @param rangeEndTimestamp - The range end.
 * @returns The formatted date, or empty.
 */
export function getWeightChartHoverDate(
   canvas: HTMLCanvasElement,
   clientX: number,
   entries: WeightEntry[],
   rangeStartTimestamp: number,
   rangeEndTimestamp: number,
) {
   if (!Number.isFinite(clientX) || entries.length === 0) {
      return ''
   }

   const rect = canvas.getBoundingClientRect()
   if (!rect.width) {
      return ''
   }

   const pointerCanvasX = (clientX - rect.left) * (canvas.width / rect.width)
   const chartWidth = canvas.width - CHART_PADDING_LEFT - CHART_PADDING_RIGHT
   if (chartWidth <= 0) {
      return ''
   }

   const clampedX = Math.min(
      Math.max(pointerCanvasX, CHART_PADDING_LEFT),
      canvas.width - CHART_PADDING_RIGHT,
   )
   const ratio = (clampedX - CHART_PADDING_LEFT) / chartWidth
   const timestamp =
      rangeStartTimestamp + (rangeEndTimestamp - rangeStartTimestamp) * ratio
   if (!Number.isFinite(timestamp)) {
      return ''
   }

   return new Date(timestamp).toLocaleDateString(getLocale(), {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
   })
}

/**
 * Finds the drawn measurement dot closest to the pointer, if it is within the
 * hit radius.
 *
 * @param canvas - The chart canvas.
 * @param clientX - The pointer client X.
 * @param clientY - The pointer client Y.
 * @param points - The dots returned by the last draw.
 * @param hitRadius - The maximum pointer distance in CSS pixels.
 * @returns The nearest dot, or null.
 */
export function findWeightChartPointAt(
   canvas: HTMLCanvasElement,
   clientX: number,
   clientY: number,
   points: WeightChartPoint[],
   hitRadius = 12,
) {
   const rect = canvas.getBoundingClientRect()
   if (!rect.width || !rect.height || points.length === 0) {
      return null
   }
   // Hit-test in CSS pixels so the radius stays the same at any backing-store size.
   const scaleX = rect.width / canvas.width
   const scaleY = rect.height / canvas.height
   const pointerX = clientX - rect.left
   const pointerY = clientY - rect.top
   let nearest: WeightChartPoint | null = null
   let nearestDistance = hitRadius
   for (const point of points) {
      const distance = Math.hypot(
         point.x * scaleX - pointerX,
         point.y * scaleY - pointerY,
      )
      if (distance <= nearestDistance) {
         nearest = point
         nearestDistance = distance
      }
   }
   return nearest
}

/**
 * Formats a measurement's date and time for the chart tooltip.
 *
 * @param timestamp - The epoch-ms timestamp.
 * @returns The localized date and time.
 */
export function formatWeightChartPointDate(timestamp: number) {
   return new Date(timestamp).toLocaleString(getLocale(), {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
   })
}

type ChartGeometry = {
   width: number
   height: number
   minTimestamp: number
   maxTimestamp: number
   toX: (timestamp: number) => number
   toY: (weight: number) => number
}

const CHART_FONT =
   '14px Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
const AXIS_FONT =
   '12px Inter, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'

/**
 * Sizes the canvas backing store to its CSS box.
 *
 * @param canvas - The chart canvas.
 * @returns The canvas size, or null when it has no layout yet.
 */
function fitCanvasToDisplay(canvas: HTMLCanvasElement) {
   const rect = canvas.getBoundingClientRect()
   const displayWidth = canvas.clientWidth || rect.width || canvas.width
   const displayHeight =
      canvas.clientHeight || rect.height || Math.round(displayWidth * 0.75)
   if (displayWidth <= 0 || displayHeight <= 0) {
      return null
   }
   if (canvas.width !== displayWidth || canvas.height !== displayHeight) {
      canvas.width = displayWidth
      canvas.height = displayHeight
   }
   return { width: canvas.width, height: canvas.height }
}

/**
 * Works out the weight axis range: from the lowest measurement (or shown
 * target) to the highest of the measurements, average, and shown target,
 * padded when flat. Without any data the axis centres on the target.
 *
 * @param weights - The plotted weights.
 * @param average - The range average, or null.
 * @param shownTarget - The target weight when it is displayed, else null.
 * @param fallbackTarget - The target weight used to centre an empty chart, or null.
 * @returns The axis bounds.
 */
function computeWeightRange(
   weights: number[],
   average: number | null,
   shownTarget: number | null,
   fallbackTarget: number | null,
) {
   const finite = (value: number | null) =>
      typeof value === 'number' && Number.isFinite(value) ? value : null
   const candidates = [
      weights.length ? Math.max(...weights) : null,
      finite(average),
      shownTarget,
   ].filter((value): value is number => value !== null)
   let maxWeight = candidates.length ? Math.max(...candidates) : Number.NaN
   let minWeight = candidates.length ? Math.min(...candidates) : Number.NaN
   if (weights.length) {
      minWeight = Math.min(...weights)
   }
   if (shownTarget !== null) {
      minWeight = Math.min(minWeight, shownTarget)
   }
   if (!Number.isFinite(minWeight) || !Number.isFinite(maxWeight)) {
      minWeight = finite(fallbackTarget) ?? 0
      maxWeight = minWeight
   }
   if (Math.abs(maxWeight - minWeight) < 0.001) {
      minWeight -= 1
      maxWeight += 1
   }
   return { minWeight, maxWeight }
}

/**
 * Builds the coordinate mapping for the plot area.
 *
 * @param size - The canvas size.
 * @param dataPoints - The plotted entries.
 * @param weightRange - The weight axis bounds.
 * @returns The chart geometry.
 */
function createChartGeometry(
   size: { width: number; height: number },
   dataPoints: WeightEntry[],
   weightRange: { minWeight: number; maxWeight: number },
): ChartGeometry {
   const { width, height } = size
   const chartWidth = width - CHART_PADDING_LEFT - CHART_PADDING_RIGHT
   const chartHeight = height - CHART_PADDING_Y * 2
   const timestamps = dataPoints.map(point => point.timestamp)
   const minTimestamp =
      timestamps.length ? Math.min(...timestamps) : Date.now() - MS_IN_DAY
   let maxTimestamp = timestamps.length ? Math.max(...timestamps) : Date.now()
   if (maxTimestamp <= minTimestamp) {
      maxTimestamp = minTimestamp + MS_IN_DAY
   }
   const timestampRange = maxTimestamp - minTimestamp
   const { minWeight, maxWeight } = weightRange
   /**
    * Maps a timestamp to a canvas x-coordinate.
    *
    * @param timestamp - The epoch-ms timestamp.
    * @returns The x coordinate.
    */
   const toX = (timestamp: number) =>
      CHART_PADDING_LEFT + ((timestamp - minTimestamp) / timestampRange) * chartWidth
   /**
    * Maps a weight to a canvas y-coordinate.
    *
    * @param weight - The weight value.
    * @returns The y coordinate.
    */
   const toY = (weight: number) => {
      const ratio = (weight - minWeight) / (maxWeight - minWeight)
      return height - CHART_PADDING_Y - ratio * chartHeight
   }
   return { width, height, minTimestamp, maxTimestamp, toX, toY }
}

/**
 * Draws the weight axis ticks and their dotted gridlines.
 *
 * @param ctx - The canvas context.
 * @param geometry - The chart geometry.
 * @param ticks - The tick weights.
 * @param formatMassLabel - Optional unit-aware label formatter.
 */
function drawWeightAxis(
   ctx: CanvasRenderingContext2D,
   geometry: ChartGeometry,
   ticks: number[],
   formatMassLabel: DrawWeightChartOptions['formatMassLabel'],
) {
   ctx.save()
   ctx.font = AXIS_FONT
   ctx.fillStyle = readThemeColor('--chart-label', '#cbd5e1')
   ctx.strokeStyle = readThemeColor('--chart-grid', 'rgba(148, 163, 184, 0.2)')
   ctx.lineWidth = 1
   ctx.textAlign = 'right'
   ctx.setLineDash([2, 5])
   for (const tick of ticks) {
      const y = geometry.toY(tick)
      ctx.beginPath()
      ctx.moveTo(CHART_PADDING_LEFT, y)
      ctx.lineTo(geometry.width - CHART_PADDING_RIGHT, y)
      ctx.stroke()
      const label =
         formatMassLabel ?
            formatMassLabel(tick)
         :  `${tick.toLocaleString(getLocale(), {
               minimumFractionDigits: 1,
               maximumFractionDigits: 1,
            })} kg`
      ctx.fillText(label, CHART_PADDING_LEFT - 8, y)
   }
   ctx.restore()
}

/**
 * Draws the vertical time markers for the selected range.
 *
 * @param ctx - The canvas context.
 * @param geometry - The chart geometry.
 * @param filterKey - The range filter.
 */
function drawTimeMarkers(
   ctx: CanvasRenderingContext2D,
   geometry: ChartGeometry,
   filterKey: WeightFilterKey,
) {
   const markers = computeWeightChartTimeMarkers(
      geometry.minTimestamp,
      geometry.maxTimestamp,
      filterKey,
   )
   ctx.save()
   ctx.strokeStyle = readThemeColor('--chart-marker', 'rgba(162, 191, 252, 0.55)')
   ctx.setLineDash([2, 6])
   ctx.lineWidth = 1
   for (const marker of markers) {
      const x = geometry.toX(marker)
      if (x <= CHART_PADDING_LEFT || x >= geometry.width - CHART_PADDING_RIGHT) {
         continue
      }
      ctx.beginPath()
      ctx.moveTo(x, CHART_PADDING_Y)
      ctx.lineTo(x, geometry.height - CHART_PADDING_Y)
      ctx.stroke()
   }
   ctx.restore()
}

/**
 * Draws a dashed horizontal guideline with an optional label.
 *
 * @param ctx - The canvas context.
 * @param geometry - The chart geometry.
 * @param value - The weight value for the line.
 * @param color - The line color.
 * @param label - Optional label text.
 */
function drawHorizontalGuide(
   ctx: CanvasRenderingContext2D,
   geometry: ChartGeometry,
   value: number,
   color: string,
   label?: string,
) {
   if (!Number.isFinite(value)) {
      return
   }
   const y = geometry.toY(value)
   ctx.strokeStyle = color
   ctx.setLineDash([4, 6])
   ctx.lineWidth = 1
   ctx.beginPath()
   ctx.moveTo(CHART_PADDING_LEFT, y)
   ctx.lineTo(geometry.width - CHART_PADDING_RIGHT, y)
   ctx.stroke()
   ctx.setLineDash([])
   if (label) {
      ctx.fillStyle = color
      ctx.textAlign = 'left'
      ctx.fillText(label, CHART_PADDING_LEFT + 4, y - 10)
   }
}

/**
 * Draws the BMI 25/30 guides. With measurements, a guide is drawn only when it
 * falls inside the plotted range (extended to the target when shown).
 *
 * @param ctx - The canvas context.
 * @param geometry - The chart geometry.
 * @param heightMeters - The user's height in metres.
 * @param weights - The plotted weights.
 * @param targetWeightKg - The target weight when shown, else null.
 */
function drawBmiGuides(
   ctx: CanvasRenderingContext2D,
   geometry: ChartGeometry,
   heightMeters: number,
   weights: number[],
   targetWeightKg: number | null,
) {
   const guides = [
      { weight: 25 * heightMeters * heightMeters, color: '#fbbf24', label: 'Overweight' },
      { weight: 30 * heightMeters * heightMeters, color: '#f87171', label: 'Obese' },
   ]
   const bounds =
      weights.length ?
         {
            min: Math.min(...weights, targetWeightKg ?? Number.POSITIVE_INFINITY),
            max: Math.max(...weights, targetWeightKg ?? Number.NEGATIVE_INFINITY),
         }
      :  null
   for (const guide of guides) {
      if (!bounds || (guide.weight >= bounds.min && guide.weight <= bounds.max)) {
         drawHorizontalGuide(
            ctx,
            geometry,
            guide.weight,
            guide.color,
            translate(guide.label),
         )
      }
   }
}

/**
 * Draws the trend line (line mode) or smoothed curve (scatter mode) plus the
 * measurement dots.
 *
 * @param ctx - The canvas context.
 * @param geometry - The chart geometry.
 * @param options - The chart options.
 * @returns The drawn measurement dots.
 */
function drawSeries(
   ctx: CanvasRenderingContext2D,
   geometry: ChartGeometry,
   options: DrawWeightChartOptions,
): WeightChartPoint[] {
   const { dataPoints, chartMode, trendAnalysis } = options
   const trend = chartMode === 'line' ? trendAnalysis : null
   if (
      trend &&
      trend.baseTimestamp !== null &&
      trend.lastTimestamp !== null &&
      trend.slopePerDay !== null &&
      trend.intercept !== null
   ) {
      const days = (trend.lastTimestamp - trend.baseTimestamp) / MS_IN_DAY
      ctx.strokeStyle = readThemeColor('--chart-trend', '#22d3ee')
      ctx.lineWidth = 2.5
      ctx.beginPath()
      ctx.moveTo(geometry.toX(trend.baseTimestamp), geometry.toY(trend.intercept))
      ctx.lineTo(
         geometry.toX(trend.lastTimestamp),
         geometry.toY(trend.intercept + days * trend.slopePerDay),
      )
      ctx.stroke()
   }
   if (chartMode === 'scatter') {
      const smoothedPoints = buildSmoothedCurveSeries(
         dataPoints,
         geometry.minTimestamp,
         geometry.maxTimestamp,
      ).map(point => ({
         x: geometry.toX(point.timestamp),
         y: geometry.toY(point.weight),
      }))
      if (smoothedPoints.length > 1) {
         ctx.strokeStyle = readThemeColor('--chart-smoothed', '#f472b6')
         ctx.lineWidth = 2
         drawCurvedLine(ctx, smoothedPoints)
      }
   }
   const points = dataPoints.map(entry => ({
      x: geometry.toX(entry.timestamp),
      y: geometry.toY(entry.weight),
      entry,
   }))
   for (const point of points) {
      ctx.fillStyle = readThemeColor('--chart-point', '#f8fafc')
      ctx.beginPath()
      ctx.arc(point.x, point.y, chartMode === 'line' ? 4 : 5, 0, Math.PI * 2)
      ctx.fill()
   }
   return points
}

/**
 * Draws the first/last date labels under the plot.
 *
 * @param ctx - The canvas context.
 * @param geometry - The chart geometry.
 */
function drawDateLabels(ctx: CanvasRenderingContext2D, geometry: ChartGeometry) {
   const dateOptions = { month: 'short', day: 'numeric' } as const
   const format = (timestamp: number) =>
      new Date(timestamp).toLocaleDateString(getLocale(), dateOptions)
   const y = geometry.height - CHART_PADDING_Y + 8
   ctx.fillStyle = readThemeColor('--chart-label-muted', '#94a3b8')
   ctx.textBaseline = 'top'
   ctx.textAlign = 'left'
   ctx.fillText(format(geometry.minTimestamp), CHART_PADDING_LEFT, y)
   ctx.textAlign = 'right'
   ctx.fillText(format(geometry.maxTimestamp), geometry.width - CHART_PADDING_RIGHT, y)
}

/**
 * Draws the full weight chart (axes, guides, points, and trend) onto a canvas.
 *
 * @param canvas - The chart canvas.
 * @param ctx - The canvas context.
 * @param options - The chart data and display options.
 * @returns The drawn measurement dots, for hover hit-testing.
 */
export function drawWeightChart(
   canvas: HTMLCanvasElement,
   ctx: CanvasRenderingContext2D,
   options: DrawWeightChartOptions,
): WeightChartPoint[] {
   const size = fitCanvasToDisplay(canvas)
   if (!size) {
      return []
   }
   const { dataPoints, heightMeters } = options
   const weights = dataPoints.map(point => point.weight)
   const shownTarget =
      (
         options.showTarget &&
         typeof options.targetWeightKg === 'number' &&
         Number.isFinite(options.targetWeightKg)
      ) ?
         options.targetWeightKg
      :  null
   const weightRange = computeWeightRange(
      weights,
      options.average,
      shownTarget,
      options.targetWeightKg,
   )
   const geometry = createChartGeometry(size, dataPoints, weightRange)

   ctx.save()
   ctx.setTransform(1, 0, 0, 1, 0, 0)
   ctx.clearRect(0, 0, size.width, size.height)
   ctx.fillStyle = readThemeColor('--chart-bg', '#0f172a')
   ctx.fillRect(0, 0, size.width, size.height)
   ctx.font = CHART_FONT
   ctx.textBaseline = 'middle'

   drawWeightAxis(
      ctx,
      geometry,
      computeWeightAxisTicks(weightRange.minWeight, weightRange.maxWeight),
      options.formatMassLabel,
   )
   drawTimeMarkers(ctx, geometry, options.filterKey)
   if (shownTarget !== null) {
      drawHorizontalGuide(
         ctx,
         geometry,
         shownTarget,
         readThemeColor('--chart-target', '#38bdf8'),
         translate('Target'),
      )
   }
   if (heightMeters) {
      drawBmiGuides(ctx, geometry, heightMeters, weights, shownTarget)
   }

   ctx.strokeStyle = readThemeColor('--chart-axis', 'rgba(148, 163, 184, 0.35)')
   ctx.lineWidth = 1
   ctx.beginPath()
   ctx.moveTo(CHART_PADDING_LEFT, CHART_PADDING_Y)
   ctx.lineTo(CHART_PADDING_LEFT, size.height - CHART_PADDING_Y)
   ctx.stroke()

   if (!dataPoints.length) {
      ctx.fillStyle = readThemeColor('--chart-label-muted', '#94a3b8')
      ctx.textAlign = 'center'
      ctx.fillText(
         translate('No entries in this range yet.'),
         size.width / 2,
         size.height / 2,
      )
      ctx.restore()
      return []
   }
   const points = drawSeries(ctx, geometry, options)
   drawDateLabels(ctx, geometry)
   ctx.restore()
   return points
}
